import {useEffect, useMemo, useState} from 'react';

import {merchantService} from '@/services/merchants';
import {userService} from '@/services/userService';
import {BusinessRFIActorType} from '@/types';
import {logRejection} from '@/utils/logRejection';

export type PoolReviewNote = {
  action: string;
  actorType: BusinessRFIActorType;
  changedBy: string;
  note?: string;
  timestamp: string;
};

/**
 * Resolves the names needed by the pool review panel:
 * - `businessName` (from the transaction's businessId) for the header
 *   "Submitted by", which identifies the merchant on the transactions screen.
 * - `resolveActor(note)` for the audit log, where each row shows the actual
 *   individual who acted: admin actors via the admin profile, business actors
 *   (the submitter) via their business-member profile.
 *
 * Falls back to the raw id / 'Admin' if a lookup fails.
 */
export const usePoolReviewActors = (
  notes: PoolReviewNote[] = [],
  businessId?: string,
) => {
  const [adminNames, setAdminNames] = useState<Record<string, string>>({});
  const [memberNames, setMemberNames] = useState<Record<string, string>>({});
  const [businessName, setBusinessName] = useState<string>('');

  const {adminIds, memberIds} = useMemo(() => {
    const collect = (actorType: BusinessRFIActorType) =>
      Array.from(
        new Set(
          notes
            .filter((n) => n.actorType === actorType)
            .map((n) => n.changedBy)
            .filter(Boolean),
        ),
      );
    return {
      adminIds: collect(BusinessRFIActorType.Admin),
      memberIds: collect(BusinessRFIActorType.Business),
    };
  }, [notes]);
  const adminIdsKey = adminIds.join(',');
  const memberIdsKey = memberIds.join(',');

  useEffect(() => {
    if (!adminIds.length && !memberIds.length && !businessId) {
      return;
    }
    let cancelled = false;

    const resolveNames = async () => {
      const [adminEntries, memberEntries, resolvedBusinessName] =
        await Promise.all([
          Promise.all(
            adminIds.map(async (id): Promise<[string, string]> => {
              try {
                const user = await userService.getUser(id);
                return [id, user?.name?.fullName || user?.currentEmail || id];
              } catch {
                return [id, id];
              }
            }),
          ),
          Promise.all(
            memberIds.map(async (id): Promise<[string, string]> => {
              try {
                const res = await merchantService.getBusinessUser(id);
                const person = res?.person;
                return [id, person?.name?.fullName || person?.email || id];
              } catch {
                return [id, id];
              }
            }),
          ),
          businessId
            ? merchantService
                .getBusinessDetails(businessId)
                .then((d) => d?.business?.name || '')
                .catch(() => '')
            : Promise.resolve(''),
        ]);
      if (!cancelled) {
        setAdminNames(Object.fromEntries(adminEntries));
        setMemberNames(Object.fromEntries(memberEntries));
        setBusinessName(resolvedBusinessName);
      }
    };

    // Every lookup above falls back to the raw id, so this handler only fires
    // if the fan-out or a setState itself throws. Names stay unresolved either
    // way; log it rather than leave the rejection unhandled.
    resolveNames().catch(
      logRejection('Failed to resolve pool review actor names'),
    );

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps use the joined string keys so this reruns only when the ids actually change, not on every new array reference
  }, [adminIdsKey, memberIdsKey, businessId]);

  const resolveActor = (note: PoolReviewNote): string => {
    // BusinessRFIActorType is shared with the RFI feature, which added a
    // Processor member. Nothing writes a Processor note on a pool review today,
    // but without this branch one would be looked up as a business member and
    // fall through to a raw id: changedBy is a rail name there, not a user id.
    if (note.actorType === BusinessRFIActorType.Processor) {
      return note.changedBy || 'Processor';
    }
    return note.actorType === BusinessRFIActorType.Admin
      ? adminNames[note.changedBy] || 'Admin'
      : memberNames[note.changedBy] || note.changedBy;
  };

  return {resolveActor, businessName};
};

export default usePoolReviewActors;
